import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, X } from "lucide-react";
import { useAuth } from "@/App";

/**
 * Section instructions banner.
 * Shows a contextual help block at the top of a page with a "Vuoi continuare a vedere queste istruzioni?" Si/No toggle.
 * The choice is persisted per-user, per-section in localStorage.
 *
 * Props:
 * - sectionKey: stable identifier for the section (e.g. "richieste", "calendario")
 * - title: heading shown in the banner
 * - items: array of strings (bullet points)
 */
export default function SectionInfoBanner({ sectionKey, title, items }) {
  const { user } = useAuth();
  const storageKey = user?.id ? `section_help_${user.id}_${sectionKey}` : null;
  // visible: undefined while loading from localStorage, true/false once resolved
  const [visible, setVisible] = useState(undefined);

  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    // Default: show banner if user never made a choice
    setVisible(saved === null ? true : saved === "1");
  }, [storageKey]);

  const handleChoice = (keepShowing) => {
    if (storageKey) {
      localStorage.setItem(storageKey, keepShowing ? "1" : "0");
    }
    if (!keepShowing) setVisible(false);
  };

  if (!visible) return null;
  if (!items || items.length === 0) return null;

  return (
    <Card className="border-2 mb-6" style={{ backgroundColor: 'var(--theme-50)', borderColor: 'var(--theme-200)' }} data-testid={`section-help-${sectionKey}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'var(--theme-100)' }}>
            <Info className="h-5 w-5" style={{ color: 'var(--theme-600)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold" style={{ color: 'var(--theme-700)' }}>{title}</h3>
            <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
              {items.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="shrink-0 mt-1 h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'var(--theme-500)' }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-slate-200">
              <p className="text-sm text-slate-600">Vuoi continuare a vedere queste istruzioni?</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleChoice(true)}
                data-testid={`section-help-yes-${sectionKey}`}
              >
                Sì
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleChoice(false)}
                data-testid={`section-help-no-${sectionKey}`}
              >
                No
              </Button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Chiudi"
            className="shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
            onClick={() => handleChoice(false)}
            data-testid={`section-help-close-${sectionKey}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
